import React, { useState, useEffect, useMemo, ComponentProps, lazy, Suspense } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import styled from 'styled-components';
import { withTheme } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import {
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Theme,
  Box,
  ButtonGroup,
  Button,
} from '@mui/material';
import { green, red } from '@mui/material/colors';
import { ValueOf } from 'type-fest';
import { spacing } from '@mui/system';

import BarChart from '../../../pages/reports/components/BarChart';
import Stats from '../../../pages/reports/components/Stats';
// Lazy load WorldMap (3MB react-jvectormap) - only used in dev mode
const WorldMap = lazy(() => import('../../../pages/reports/components/WorldMap'));
import SimpleList, { ListItemTypes } from 'components/SimpleList';

import ServiceMetricsTable from 'IntegrationModule/components/ServiceMetricsTable';
import { ErrorLogTable } from 'shared';
import { formatIntegrationHealthDateParams } from 'utils';
import { integrationHealthIsoStateModifiers } from 'IntegrationModule/utils';
import { openUrl } from 'utils';
import useLocalDispatch from 'hooks/useLocalDispatch';
import uniqBy from 'lodash/uniqBy';
import OverviewHealthTable from 'IntegrationModule/components/OverviewHealth/OverviewHealthTable';
import OverviewAggregateHealthCalendar from 'IntegrationModule/components/OverviewHealth/OverviewAggregateHealthCalendar';

import {
  INTEGRATION_SERVICE_METRICS_API_DEFAULT_VALUES,
  intervals,
  GENERAL,
  integrationHealthIsoStateIds,
  INTEGRATION_SERVICE_NAMES,
  GENERAL_INTEGRATION_DEFAULT_TABLE_VIEW,
  GENERAL_INTEGRATIONS_TABLE_VIEWS_ARRAY,
  GENERAL_INTEGRATIONS_TABLE_VIEWS,
} from '@constants';

import {
  fetchIntegrationServiceMetrics,
  fetchIntegrationServiceMetricsTotal,
  fetchIntegrationHealth,
  fetchTeams,
} from 'thunk';

import {
  selectIntegrationAgentDomains,
  selectIsDevMode,
  selectSyncOverviewCountsData,
  selectTeamHash,
} from 'selectors';
import { selectServiceMetricsTotalRaw } from 'IntegrationModule/selectors/general';

import useTransitionMenu from 'hooks/useTransitionMenu';
import useServiceMetricsTable from 'hooks/useServiceMetricsTable';
import useErrorLogs from 'hooks/useErrorLogs';
import useUrl from 'hooks/useUrl';
import { ErrorInterface, FilterStateInterface, RecentErrorsType } from 'types';
import useGeneralIntegrationPageContext from 'hooks/useGeneralIntegrationPageContext';
import { ToggleOptionsButton } from 'components/ToggleOptionsButton';
import StandardPageHeader from 'components/StandardPageHeader';
import { useIntegrationMetadataQuery } from 'IntegrationModule/api/queries/useIntegrationMetadataQuery';

const Divider = styled(MuiDivider)(spacing as any);

const Typography = styled(MuiTypography)(spacing);

function OverallIntegrationDetails({ theme }: { theme: Theme }) {
  const dispatch = useAppDispatch();
  const isDevMode = useAppSelector(selectIsDevMode);
  const { pushQueryParams } = useUrl();
  const { dispatchLocal } = useLocalDispatch();
  const [integrationTableView, setIntegrationTableView] = useState(
    GENERAL_INTEGRATION_DEFAULT_TABLE_VIEW,
  );
  const { isRequesting: isRequestingSyncOverviewCountsData } = useAppSelector(
    selectSyncOverviewCountsData,
  );
  const integrationAgentDomains = useAppSelector(selectIntegrationAgentDomains);
  const { metadataFor } = useIntegrationMetadataQuery();
  const { data: serviceMetricsTotal } = useAppSelector(selectServiceMetricsTotalRaw);
  const allTargetServiceIdsForService: number[] = useMemo(
    () =>
      Array.from(new Set(serviceMetricsTotal.map((d) => Number(d.targetServiceId)))).filter(
        (n) => Number.isFinite(n) && n > 0,
      ),
    [serviceMetricsTotal],
  );

  /** Target Service and Interval */
  const { intervalToUse, targetService, selectedIntegration } = useGeneralIntegrationPageContext();

  /* --------------------------- Integration Health --------------------------- */
  const { renderTransitionMenu } = useTransitionMenu({
    accessorFn: (item: Record<string, any>) => item.label,
    defaultSelectedItem: intervalToUse,
  });

  useEffect(() => {
    dispatch(
      fetchIntegrationServiceMetrics({
        offset: INTEGRATION_SERVICE_METRICS_API_DEFAULT_VALUES.OFFSET,
        limit: INTEGRATION_SERVICE_METRICS_API_DEFAULT_VALUES.LIMIT,
        interval: intervalToUse.id,
        targetService,
        // Spec: Health is only for today
        onSuccess: ({
          response,
        }: {
          response: {
            data: {
              teams: { mosaicTeamId: number; targetServiceId: number }[];
            };
          };
        }) => {
          const { teams } = response.data;
          const { mosaicTeamIds, targetServiceIds } = teams.reduce<{
            mosaicTeamIds: number[];
            targetServiceIds: number[];
          }>(
            (acc, curr) => {
              const { mosaicTeamId, targetServiceId } = curr;
              acc.mosaicTeamIds.push(mosaicTeamId);
              acc.targetServiceIds.push(targetServiceId);
              return acc;
            },
            { mosaicTeamIds: [], targetServiceIds: [] },
          );

          dispatch(
            fetchTeams({
              teamIds: mosaicTeamIds,
            }),
          );

          dispatch(
            fetchIntegrationHealth({
              targetService,
              targetServiceIds,
              serviceNames: [targetService],
              startDate: formatIntegrationHealthDateParams(new Date()),
              endDate: formatIntegrationHealthDateParams(new Date()),
              isoStateId: integrationHealthIsoStateIds.SyncRatioTable,
              modifier:
                integrationHealthIsoStateModifiers[integrationHealthIsoStateIds.SyncRatioTable],
            }),
          );
        },
      }),
    );

    // Sync overview fetch
    dispatch(
      fetchIntegrationServiceMetricsTotal({
        targetService,
        interval: intervalToUse.id,
        onSuccess: ({ response }: { response: { data: { targetServiceId: number }[] } }) => {
          const { data } = response;

          dispatch(
            fetchIntegrationHealth({
              targetService,
              serviceNames: [targetService],
              targetServiceIds: uniqBy(data, (d) => d.targetServiceId).map(
                (d) => d.targetServiceId,
              ),
              startDate: formatIntegrationHealthDateParams(new Date()),
              endDate: formatIntegrationHealthDateParams(new Date()),
              isoStateId: integrationHealthIsoStateIds.TotalCountTable,
              modifier:
                integrationHealthIsoStateModifiers[integrationHealthIsoStateIds.TotalCountTable],
            }),
          );
        },
      }),
    );
  }, [dispatch, intervalToUse.id, intervalToUse.value, targetService]);

  const {
    tableData: serviceMetricsTableData,
    _customRowProps: serviceMetricsCustomRowProps,
    integrationServiceMetricsTableError,
    isIntegrationServiceMetricsTableLoading,
  } = useServiceMetricsTable({
    currentService: targetService,
    selectedInterval: intervalToUse.id,
  });
  const {
    isLoading: errorLogLoading,
    error: errorLogError,
    data: errorLogData,
    filterState: errorLogFilter,
    collapseProps: errorLogDataCollapseProps,
    total: errorLogTotalCounts,
    headCells: errorLogHeadCells,
    filtersToShow: errorLogFiltersToShow,
    onRequestSort: errorLogOnRequestSort,
    onPageChange: errorLogOnPageChange,
    onAllCollapse: errorLogOnAllCollapse,
    fetchNew: errorLogFetchNew,
    errorType,
  } = useErrorLogs({
    targetServiceName: targetService,
    viewBy: GENERAL,
  });

  /** Selecting a new integration */
  const handleClickLink = (item: ListItemTypes) => {
    if (item.id) {
      openUrl(item.id as string);
    }
  };

  /** Selecting a new interval */
  const handleMenuItemClick = (interval: Record<string, any>) => {
    const { id: newSelectedIntervalId } = interval;

    if (newSelectedIntervalId !== intervalToUse.id) {
      pushQueryParams({
        targetService: targetService,
        interval: newSelectedIntervalId,
      });
    }
  };

  /**
   * Per-integration metadata sourced from galaxy-server's metadata endpoint.
   * Items are only rendered when the underlying URL is present so legacy
   * integrations without entries simply show a shorter list.
   */
  const integrationLinks = useMemo(() => {
    const meta = metadataFor(targetService);
    const isQbSelected =
      selectedIntegration.id === INTEGRATION_SERVICE_NAMES.QUICKBOOKS_DESKTOP ||
      selectedIntegration.id === INTEGRATION_SERVICE_NAMES.QUICKBOOKS_ONLINE;
    const queueLinkUrl = isQbSelected
      ? ''
      : integrationAgentDomains[selectedIntegration.id]?.url
        ? `${integrationAgentDomains[selectedIntegration.id].url}/admin/queues`
        : '';

    const items: Array<{ id: string; label: string; openInNewTab: boolean }> = [];

    if (meta.externalDocsUrl) {
      items.push({ id: meta.externalDocsUrl, label: 'External Documentation', openInNewTab: true });
    }
    if (meta.internalDocsUrl) {
      items.push({
        id: meta.internalDocsUrl,
        label: `Mosaic Internal Site - ${selectedIntegration.label}`,
        openInNewTab: true,
      });
    }
    if (meta.runbookUrl) {
      items.push({ id: meta.runbookUrl, label: 'Runbook', openInNewTab: true });
    }
    if (meta.githubRepoUrl) {
      items.push({ id: meta.githubRepoUrl, label: 'GitHub Repository', openInNewTab: true });
    }
    if (meta.grafanaDashboardUrl) {
      items.push({ id: meta.grafanaDashboardUrl, label: 'Grafana Dashboard', openInNewTab: true });
    }
    if (queueLinkUrl) {
      items.push({
        id: queueLinkUrl,
        label: `Queue Link - ${selectedIntegration.label}`,
        openInNewTab: true,
      });
    }

    return items;
  }, [
    metadataFor,
    targetService,
    integrationAgentDomains,
    selectedIntegration.id,
    selectedIntegration.label,
  ]);

  const isLoading = isIntegrationServiceMetricsTableLoading || isRequestingSyncOverviewCountsData;

  const generalIntegrationTableHash = {
    [GENERAL_INTEGRATIONS_TABLE_VIEWS.OVERVIEW]: (
      <OverviewHealthTable targetService={targetService} isLoading={isLoading} />
    ),
    [GENERAL_INTEGRATIONS_TABLE_VIEWS.SYNC_RATIO]: (
      <ServiceMetricsTable
        data={serviceMetricsTableData}
        isLoading={isIntegrationServiceMetricsTableLoading}
        _customRowProps={serviceMetricsCustomRowProps}
      />
    ),
    [GENERAL_INTEGRATIONS_TABLE_VIEWS.ERRORS]: (
      <ErrorLogTable
        targetService={targetService}
        filterState={errorLogFilter as FilterStateInterface}
        collapseProps={errorLogDataCollapseProps}
        filtersToShow={errorLogFiltersToShow}
        total={errorLogTotalCounts}
        error={errorLogError as ErrorInterface}
        isLoading={errorLogLoading}
        headCells={errorLogHeadCells}
        data={errorLogData as unknown as Array<RecentErrorsType>}
        tableTitle="Error Log"
        onPageChange={errorLogOnPageChange}
        onRequestSort={errorLogOnRequestSort}
        onAllCollapse={errorLogOnAllCollapse}
        errorType={errorType}
        onFetchNew={errorLogFetchNew}
      />
    ),
  };

  const TableComponent = generalIntegrationTableHash[integrationTableView];

  const onViewToggle: ComponentProps<typeof ToggleOptionsButton>['onSelect'] = ({ option }) => {
    setIntegrationTableView(option.value);
  };

  const renderViewToggle = () => {
    return (
      <ToggleOptionsButton
        options={GENERAL_INTEGRATIONS_TABLE_VIEWS_ARRAY}
        currentOption={integrationTableView}
        onSelect={onViewToggle}
        isDisabled={isLoading}
      />
    );
  };

  return (
    <React.Fragment>
      <Helmet title={`${selectedIntegration.label}`} />

      <StandardPageHeader
        title={selectedIntegration.label}
        subtitle={`Monitor and manage ${selectedIntegration.label} integration`}
        icon="Integration"
        sx={{ mb: 6 }}
      />

      <Grid justifyContent="space-between" container spacing={3} sx={{ mb: 3 }}>
        <Grid size="auto">
          {renderTransitionMenu({
            isLoading,
            menuItems: intervals,
            handleCustomMenuItemClick: handleMenuItemClick,
          })}
        </Grid>
      </Grid>
      <Grid style={{ marginBottom: '1rem', whiteSpace: 'nowrap' }} spacing={3} container>
        <Grid size={12}>
          <SimpleList
            listTitle="Documentation & Resources"
            listSubtitle=""
            listItems={integrationLinks}
            handleSelectListItem={handleClickLink}
          />
        </Grid>
        <Grid size={12}>
          <OverviewAggregateHealthCalendar
            targetService={targetService}
            targetServiceIds={allTargetServiceIdsForService}
          />
        </Grid>
        <Grid size={12}>
          <Box mb={5} display={'flex'}>
            {renderViewToggle()}
          </Box>
          {TableComponent}
        </Grid>
      </Grid>
      <Divider sx={{ my: 6 }} />
      <Grid style={{ display: isDevMode ? 'flex' : 'none' }} container spacing={6}>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Stats
            title="Visitors"
            amount="24.532"
            chip="Today"
            percentageText="+14%"
            percentagecolor={green[500]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Stats
            title="Activity"
            amount="63.200"
            chip="Annual"
            percentageText="-12%"
            percentagecolor={red[500]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Stats
            title="Real-Time"
            amount="1.320"
            chip="Monthly"
            percentageText="-18%"
            percentagecolor={red[500]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Stats
            title="Bounce"
            amount="12.364"
            chip="Yearly"
            percentageText="+27%"
            percentagecolor={green[500]}
          />
        </Grid>
      </Grid>
      <Grid style={{ display: isDevMode ? 'flex' : 'none' }} container spacing={6}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Suspense fallback={<div>Loading map...</div>}>
            <WorldMap />
          </Suspense>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <BarChart />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default withTheme(OverallIntegrationDetails);
